import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Box,
  Stack,
  Typography,
} from "@mui/material";

import * as maplibregl from "maplibre-gl";

import type {
  GeoJSONSource,
  Map as MapLibreMap,
  Popup as MapLibrePopup,
} from "maplibre-gl";

import { createPortal } from "react-dom";

import BasePaper from "components/Base/BasePaper";

import MapPopup from "./MapPopup";

import { Address } from "api/Community/CommunityInterfaces";

import { useTheme } from "@mui/material/styles";


interface MapDetailProps {
  data: Address[];
}


/* ========================================================================== */
/* Active popup                                                               */
/* ========================================================================== */

interface ActivePopup {
  container: HTMLDivElement;

  id: number;

  streetAddress?: string;
  city?: string;
  state?: string;
  postCode?: string;
  country?: string;
  typeOfBuilding?: string;
}


const MapDetail: React.FC<MapDetailProps> = ({
  data,
}) => {
  const theme = useTheme();


  const mapContainerRef =
    useRef<HTMLDivElement | null>(null);

  const mapRef =
    useRef<MapLibreMap | null>(null);

  const popupRef =
    useRef<MapLibrePopup | null>(null);


  const [
    activePopup,
    setActivePopup,
  ] = useState<ActivePopup | null>(
    null
  );


  /* -------------------------------------------------------------------------- */
  /* Addresses that can actually be shown on the map                            */
  /* -------------------------------------------------------------------------- */

  const mappedAddresses =
    useMemo(
      () =>
        data.filter(
          (address) =>
            address.latitude !== null &&
            address.longitude !== null &&
            Number.isFinite(
              address.latitude
            ) &&
            Number.isFinite(
              address.longitude
            )
        ),
      [data]
    );


  /* -------------------------------------------------------------------------- */
  /* Create map                                                                 */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    if (!mapContainerRef.current) {
      return;
    }

    if (mapRef.current) {
      return;
    }


    const mapTilerKey =
      process.env
        .REACT_APP_MAPTILER_API_KEY;


    if (!mapTilerKey) {
      console.error(
        "MapTiler API key is missing. Check REACT_APP_MAPTILER_API_KEY in your .env file."
      );

      return;
    }


    const map =
      new maplibregl.Map({
        container:
          mapContainerRef.current,

        style:
          `https://api.maptiler.com/maps/01a02607-b3f5-7862-afcd-acc1fdccdef8/style.json?key=${mapTilerKey}`,

        center: [-2.5, 54.5],

        zoom: 5,
      });


    /* -------------------- Navigation controls -------------------- */

    map.addControl(
      new maplibregl.NavigationControl({
        showCompass: true,
        showZoom: true,
      }),
      "top-right"
    );


    /* -------------------- Map events -------------------- */

    map.on(
      "load",
      () => {
        console.log(
          "MapTiler map loaded successfully"
        );
      }
    );


    map.on(
      "error",
      (event) => {
        console.error(
          "MapLibre / MapTiler error:",
          event.error
        );
      }
    );


    mapRef.current = map;


    /* -------------------- Cleanup -------------------- */

    return () => {
      popupRef.current?.remove();

      popupRef.current = null;

      map.remove();

      mapRef.current = null;
    };
  }, []);


  /* -------------------------------------------------------------------------- */
  /* Add addresses to map                                                       */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const map =
      mapRef.current;

    if (!map) {
      return;
    }


    /* ------------------------------------------------------------------------ */
    /* Convert addresses to GeoJSON                                             */
    /* ------------------------------------------------------------------------ */

    const geoJson: GeoJSON.FeatureCollection<
      GeoJSON.Point,
      {
        id: number;

        street_address: string;
        city: string;
        state: string;
        post_code: string;
        country: string;

        type_of_building: string;
      }
    > = {
      type: "FeatureCollection",

      features:
        mappedAddresses.map(
          (address) => ({
            type: "Feature",

            geometry: {
              type: "Point",

              coordinates: [
                address.longitude as number,
                address.latitude as number,
              ],
            },

            properties: {
              id:
                address.id,

              street_address:
                address.street_address,

              city:
                address.city,

              state:
                address.state,

              post_code:
                address.post_code,

              country:
                address.country,

              type_of_building:
                address.type_of_building,
            },
          })
        ),
    };


    /* ------------------------------------------------------------------------ */
    /* Update map                                                               */
    /* ------------------------------------------------------------------------ */

    const updateMap = () => {
      const existingSource =
        map.getSource(
          "addresses"
        ) as GeoJSONSource | undefined;


      /* -------------------- Existing source -------------------- */

      if (existingSource) {
        existingSource.setData(
          geoJson
        );

        fitMapToAddresses(
          map,
          mappedAddresses
        );

        return;
      }


      /* -------------------- Add source -------------------- */

      map.addSource(
        "addresses",
        {
          type: "geojson",

          data:
            geoJson,

          cluster:
            false,
        }
      );


      /* -------------------- Address marker layer -------------------- */

      map.addLayer({
        id:
          "address-points",

        type:
          "circle",

        source:
          "addresses",

        paint: {
          "circle-radius": [
            "interpolate",

            ["linear"],

            ["zoom"],

            5,
            4,

            10,
            6,

            15,
            9,

            18,
            12,
          ],

          "circle-color":
            theme.palette.primary.main,

          "circle-stroke-color":
            theme.palette.text.primary,

          "circle-stroke-width":
            1,

          "circle-opacity":
            0.9,
        },
      });


      /* ---------------------------------------------------------------------- */
      /* Pointer cursor                                                         */
      /* ---------------------------------------------------------------------- */

      map.on(
        "mouseenter",
        "address-points",
        () => {
          map.getCanvas().style.cursor =
            "pointer";
        }
      );


      map.on(
        "mouseleave",
        "address-points",
        () => {
          map.getCanvas().style.cursor =
            "";
        }
      );


      /* ---------------------------------------------------------------------- */
      /* Address popup                                                          */
      /* ---------------------------------------------------------------------- */

      map.on(
        "click",
        "address-points",
        (event) => {
          const feature =
            event.features?.[0];


          if (!feature) {
            return;
          }


          const geometry =
            feature.geometry as GeoJSON.Point;


          const coordinates =
            geometry.coordinates.slice() as [
              number,
              number
            ];


          const properties =
            feature.properties;


          if (!properties) {
            return;
          }


          /* ------------------------------------------------------------------ */
          /* Remove previous popup                                              */
          /* ------------------------------------------------------------------ */

          popupRef.current?.remove();

          popupRef.current = null;

          setActivePopup(
            null
          );


          /* ------------------------------------------------------------------ */
          /* Container that MapLibre positions                                  */
          /* ------------------------------------------------------------------ */

          const popupContainer =
            document.createElement(
              "div"
            );


          /* ------------------------------------------------------------------ */
          /* Create MapLibre popup                                              */
          /* ------------------------------------------------------------------ */

          const popup =
            new maplibregl.Popup({
              closeButton: false,

              closeOnClick: true,

              offset: 10,

              className:
                "pathways-address-popup",
            })
              .setLngLat(
                coordinates
              )
              .setDOMContent(
                popupContainer
              )
              .addTo(
                map
              );


          popupRef.current =
            popup;


          /* ------------------------------------------------------------------ */
          /* Give React the popup data                                          */
          /* ------------------------------------------------------------------ */

          setActivePopup({
            container:
              popupContainer,

            id:
              Number(
                properties.id
              ),

            streetAddress:
              properties.street_address,

            city:
              properties.city,

            state:
              properties.state,

            postCode:
              properties.post_code,

            country:
              properties.country,

            typeOfBuilding:
              properties.type_of_building,
          });


          /* ------------------------------------------------------------------ */
          /* Style MapLibre wrapper                                             */
          /* ------------------------------------------------------------------ */

          const popupElement =
            popup.getElement();


          const popupContent =
            popupElement.querySelector(
              ".maplibregl-popup-content"
            ) as HTMLElement | null;


          if (popupContent) {
            popupContent.style
              .backgroundColor =
              theme.custom.base.background.paper.dark;

            popupContent.style.color =
              theme.custom.base.text.primary.dark;

            popupContent.style.padding =
              "14px";
          }


          /* -------------------- Popup triangle -------------------- */

          const popupTip =
            popupElement.querySelector(
              ".maplibregl-popup-tip"
            ) as HTMLElement | null;


          if (popupTip) {
            popupTip.style.borderTopColor =
              theme.custom.base.background.paper.dark;

            popupTip.style.borderBottomColor =
              theme.custom.base.background.paper.dark;
          }


          /* ------------------------------------------------------------------ */
          /* Popup close                                                        */
          /* ------------------------------------------------------------------ */

          popup.on(
            "close",
            () => {
              popupRef.current =
                null;

              setActivePopup(
                null
              );
            }
          );
        }
      );


      /* ---------------------------------------------------------------------- */
      /* Fit map around addresses                                               */
      /* ---------------------------------------------------------------------- */

      fitMapToAddresses(
        map,
        mappedAddresses
      );
    };


    /* ------------------------------------------------------------------------ */
    /* Wait for style                                                           */
    /* ------------------------------------------------------------------------ */

    if (
      map.isStyleLoaded()
    ) {
      updateMap();
    } else {
      map.once(
        "load",
        updateMap
      );
    }
  }, [
    mappedAddresses,
    theme,
  ]);


  /* -------------------------------------------------------------------------- */
  /* Render                                                                     */
  /* -------------------------------------------------------------------------- */

  return (
    <Box
      sx={{
        mx: 2,
        my: 2,
      }}
    >
      {/* -------------------------------------------------------------------- */}
      {/* Summary                                                              */}
      {/* -------------------------------------------------------------------- */}

      <BasePaper>
        <Stack
          direction="column"
          spacing={2}
          mb={1}
        >
          <Typography variant="h3">
            Map
          </Typography>

          <Typography variant="body2">
            A visual representation of your community.
          </Typography>
        </Stack>
      </BasePaper>


      {/* -------------------------------------------------------------------- */}
      {/* Map                                                                  */}
      {/* -------------------------------------------------------------------- */}

      <BasePaper>
        <Box
          sx={{
            width:
              "100%",

            height: {
              xs:
                "60vh",

              md:
                "72vh",
            },

            minHeight: {
              xs:
                450,

              md:
                600,
            },

            borderRadius:
              2,

            overflow:
              "hidden",

            position:
              "relative",
          }}
        >
          <Box
            ref={
              mapContainerRef
            }
            sx={{
              position:
                "absolute",

              inset:
                0,

              width:
                "100%",

              height:
                "100%",
            }}
          />


          {/* ---------------------------------------------------------------- */}
          {/* No coordinates message                                           */}
          {/* ---------------------------------------------------------------- */}

          {data.length > 0 &&
            mappedAddresses.length ===
              0 && (
              <Box
                sx={{
                  position:
                    "absolute",

                  left:
                    "50%",

                  top:
                    "50%",

                  transform:
                    "translate(-50%, -50%)",

                  backgroundColor:
                    "background.paper",

                  px:
                    3,

                  py:
                    2,

                  borderRadius:
                    2,

                  boxShadow:
                    3,

                  textAlign:
                    "center",

                  zIndex:
                    10,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight:
                      600,
                  }}
                >
                  No mapped addresses
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt:
                      0.5,
                  }}
                >
                  These addresses do not
                  currently have latitude and
                  longitude values.
                </Typography>
              </Box>
            )}


          {/* ---------------------------------------------------------------- */}
          {/* React popup portal                                                */}
          {/* ---------------------------------------------------------------- */}

          {activePopup &&
            createPortal(
              <MapPopup
                id={
                  activePopup.id
                }
                streetAddress={
                  activePopup.streetAddress
                }
                city={
                  activePopup.city
                }
                state={
                  activePopup.state
                }
                postCode={
                  activePopup.postCode
                }
                country={
                  activePopup.country
                }
                typeOfBuilding={
                  activePopup.typeOfBuilding
                }
              />,

              activePopup.container
            )}
        </Box>
      </BasePaper>
    </Box>
  );
};


/* ========================================================================== */
/* Fit map around addresses                                                   */
/* ========================================================================== */

const fitMapToAddresses = (
  map: MapLibreMap,
  addresses: Address[]
) => {
  if (
    addresses.length === 0
  ) {
    return;
  }


  /* -------------------------------------------------------------------------- */
  /* One address                                                                */
  /* -------------------------------------------------------------------------- */

  if (
    addresses.length === 1
  ) {
    const address =
      addresses[0];


    if (
      address.latitude === null ||
      address.longitude === null
    ) {
      return;
    }


    map.easeTo({
      center: [
        address.longitude,
        address.latitude,
      ],

      zoom:
        15,
    });


    return;
  }


  /* -------------------------------------------------------------------------- */
  /* Multiple addresses                                                         */
  /* -------------------------------------------------------------------------- */

  const bounds =
    new maplibregl.LngLatBounds();


  addresses.forEach(
    (address) => {
      if (
        address.latitude === null ||
        address.longitude === null
      ) {
        return;
      }


      bounds.extend([
        address.longitude,
        address.latitude,
      ]);
    }
  );


  if (
    bounds.isEmpty()
  ) {
    return;
  }


  map.fitBounds(
    bounds,
    {
      padding: {
        top:
          70,

        bottom:
          70,

        left:
          70,

        right:
          70,
      },

      maxZoom:
        16,

      duration:
        800,
    }
  );
};


export default MapDetail;